// Pagina admin /patients: lista paginata, filtri, ricerca per pseudonym + nome.
// La ricerca per nome richiede decifratura (audit log) e per ora è limitata
// agli admin master (vedi backend admin-patients.ts).

import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { DogEarCard, RuleBicolor } from '@audiq/ui';
import { api } from '../lib/api';

const PAGE_SIZE = 20;

export function PatientsPage() {
  const [search, setSearch] = useState('');
  const [searchActive, setSearchActive] = useState('');
  const [redFlags, setRedFlags] = useState<'' | 'true' | 'false'>('');
  const [language, setLanguage] = useState('');
  const [page, setPage] = useState(1);

  const list = useQuery({
    queryKey: ['patients', { search: searchActive, redFlags, language, page }],
    queryFn: () =>
      api.listPatients({
        search: searchActive || undefined,
        has_red_flags: (redFlags || undefined) as 'true' | 'false' | undefined,
        language: language || undefined,
        page,
        limit: PAGE_SIZE,
      }),
  });

  const totalPages = useMemo(() => {
    const total = list.data?.total ?? 0;
    return Math.max(1, Math.ceil(total / PAGE_SIZE));
  }, [list.data]);

  function fmtDate(s?: string | null) {
    if (!s) return '—';
    try {
      return new Date(s).toLocaleString('it-IT', { dateStyle: 'short', timeStyle: 'short' });
    } catch {
      return s;
    }
  }

  return (
    <div className="mx-auto max-w-6xl">
      <div className="mono mb-2" style={{ color: 'var(--mute)' }}>
        Pazienti
      </div>
      <h2 className="serif text-3xl" style={{ color: 'var(--ink)' }}>
        Scheda paziente
      </h2>
      <p className="mt-2 text-sm" style={{ color: 'var(--mute)' }}>
        Lista pazienti con storico encounter, identità decifrata su richiesta, e note operatore.
        Ogni accesso a identità è loggato in <span className="mono">audit_log</span>.
      </p>

      <RuleBicolor className="my-6" />

      {/* Filtri */}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          setSearchActive(search.trim());
          setPage(1);
        }}
        className="mb-4 flex flex-wrap items-end gap-2"
      >
        <div className="flex-1 min-w-[280px]">
          <label className="mono mb-1 block text-xs" style={{ color: 'var(--mute)' }}>
            Cerca (pseudonym o nome)
          </label>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="t8K2-mP9X-vN3w  oppure  Mario Rossi"
            className="w-full rounded-md border px-3 py-2 outline-none"
            style={{
              background: 'var(--paper)',
              borderColor: 'var(--line)',
              color: 'var(--ink)',
            }}
          />
        </div>
        <div>
          <label className="mono mb-1 block text-xs" style={{ color: 'var(--mute)' }}>
            Red flag
          </label>
          <select
            value={redFlags}
            onChange={(e) => {
              setRedFlags(e.target.value as '' | 'true' | 'false');
              setPage(1);
            }}
            className="rounded-md border px-3 py-2"
            style={{ background: 'var(--paper)', borderColor: 'var(--line)', color: 'var(--ink)' }}
          >
            <option value="">Tutti</option>
            <option value="true">Con critical</option>
            <option value="false">Senza critical</option>
          </select>
        </div>
        <div>
          <label className="mono mb-1 block text-xs" style={{ color: 'var(--mute)' }}>
            Lingua
          </label>
          <input
            value={language}
            onChange={(e) => {
              setLanguage(e.target.value);
              setPage(1);
            }}
            placeholder="it / ar / en…"
            className="w-32 rounded-md border px-3 py-2"
            style={{ background: 'var(--paper)', borderColor: 'var(--line)', color: 'var(--ink)' }}
          />
        </div>
        <button
          type="submit"
          className="rounded-md px-4 py-2 text-sm font-semibold"
          style={{ background: 'var(--primary)', color: '#fff' }}
        >
          Applica
        </button>
        {searchActive ? (
          <button
            type="button"
            onClick={() => {
              setSearch('');
              setSearchActive('');
              setPage(1);
            }}
            className="rounded-md border px-4 py-2 text-sm"
            style={{ borderColor: 'var(--line)', color: 'var(--mute)' }}
          >
            Annulla
          </button>
        ) : null}
      </form>

      {searchActive ? (
        <div className="mb-3 mono text-xs" style={{ color: 'var(--mute)' }}>
          Ricerca per nome richiede decifratura — riservata ad admin master.
        </div>
      ) : null}

      {/* Lista */}
      {list.isLoading ? (
        <p className="mt-8 text-sm" style={{ color: 'var(--mute)' }}>
          Caricamento…
        </p>
      ) : list.isError ? (
        <p className="mt-8 text-sm" style={{ color: 'var(--danger)' }}>
          Errore caricamento pazienti
        </p>
      ) : list.data && list.data.patients.length === 0 ? (
        <DogEarCard
          className="mt-8 rounded-md border p-6 text-center"
          style={{ background: 'var(--paper)', borderColor: 'var(--line)', color: 'var(--mute)' }}
        >
          Nessun paziente trovato.
        </DogEarCard>
      ) : (
        <div className="overflow-hidden rounded-md border" style={{ borderColor: 'var(--line)' }}>
          <table className="w-full text-sm">
            <thead style={{ background: 'var(--soft)' }}>
              <tr className="mono text-xs" style={{ color: 'var(--mute)' }}>
                <th className="px-3 py-2 text-start">Pseudonym</th>
                <th className="px-3 py-2 text-start">Nome</th>
                <th className="px-3 py-2 text-start">Lingue</th>
                <th className="px-3 py-2 text-end">Encounter</th>
                <th className="px-3 py-2 text-end">Critical</th>
                <th className="px-3 py-2 text-start">Ultimo accesso</th>
              </tr>
            </thead>
            <tbody>
              {list.data?.patients.map((p) => (
                <tr
                  key={p.id}
                  className="border-t hover:bg-[color:var(--soft)]"
                  style={{ borderColor: 'var(--line)' }}
                >
                  <td className="px-3 py-2">
                    <Link
                      to={`/patients/${encodeURIComponent(p.pseudonym)}`}
                      className="mono"
                      style={{ color: 'var(--primary)' }}
                    >
                      {p.pseudonym}
                    </Link>
                  </td>
                  <td className="px-3 py-2" style={{ color: 'var(--ink)' }}>
                    {p.first_name || p.last_name ? (
                      <>
                        {p.first_name ?? ''} {p.last_name ?? ''}
                      </>
                    ) : p.has_identity ? (
                      <span className="mono text-xs" style={{ color: 'var(--mute)' }}>
                        identità raccolta — apri la scheda
                      </span>
                    ) : (
                      <span style={{ color: 'var(--mute)' }}>—</span>
                    )}
                  </td>
                  <td className="px-3 py-2 mono text-xs" style={{ color: 'var(--mute)' }}>
                    {p.languages.join(', ') || '—'}
                  </td>
                  <td className="px-3 py-2 text-end mono" style={{ color: 'var(--ink)' }}>
                    {p.encounters_count}
                  </td>
                  <td className="px-3 py-2 text-end mono">
                    {p.red_flags_count > 0 ? (
                      <span style={{ color: 'var(--danger)' }}>{p.red_flags_count}</span>
                    ) : (
                      <span style={{ color: 'var(--mute)' }}>0</span>
                    )}
                  </td>
                  <td className="px-3 py-2" style={{ color: 'var(--mute)' }}>
                    {fmtDate(p.last_encounter_at ?? p.last_seen_at)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {list.data && list.data.total > PAGE_SIZE ? (
        <div className="mt-4 flex items-center justify-between">
          <div className="mono text-xs" style={{ color: 'var(--mute)' }}>
            {list.data.total} pazienti — pagina {page} di {totalPages}
          </div>
          <div className="flex gap-2">
            <button
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="rounded-md border px-3 py-1 text-sm disabled:opacity-50"
              style={{ borderColor: 'var(--line)', color: 'var(--ink)' }}
            >
              ← Prec
            </button>
            <button
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
              className="rounded-md border px-3 py-1 text-sm disabled:opacity-50"
              style={{ borderColor: 'var(--line)', color: 'var(--ink)' }}
            >
              Succ →
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
